import React, { useState } from 'react';
import {
    McDialog,
    McButton,
    McIcon,
    McSelect,
    McLoadingIndicator,
    McTag,
} from '@maersk-global/mds-react-wrapper';
import { useQuery } from '@tanstack/react-query';
import { queryService } from '../../../services/queryService';
import { ScheduleQueryResponse } from '../../../types';
import { DownloadResultsButton } from '../../Common/DownloadResultsButton';

interface StoredResultsDialogProps {
    schedule: ScheduleQueryResponse | null;
    open: boolean;
    onClose: () => void;
}

export const StoredResultsDialog: React.FC<StoredResultsDialogProps> = ({
    schedule,
    open,
    onClose,
}) => {
    const [selectedTerminal, setSelectedTerminal] = useState<string>('');

    // Set default terminal when schedule changes
    React.useEffect(() => {
        if (schedule && schedule.terminalIds.length > 0) {
            setSelectedTerminal(schedule.terminalIds[0]);
        }
    }, [schedule]);

    // Fetch results for the selected terminal and schedule
    const { data: results, isLoading, refetch } = useQuery({
        queryKey: ['scheduleResults', schedule?.scheduleId, selectedTerminal],
        queryFn: async () => {
            if (!schedule || !selectedTerminal) return [];
            // fetching stored results using getResults API matching the resultTableName
            // We assume the backend handles the mapping or we query the result table directly.
            // The method getResults(tableName, page, size, terminalId) looks appropriate.
            try {
                const response = await queryService.getResults(
                    schedule.resultTableName,
                    0,
                    100,
                    selectedTerminal
                );
                // response might be PageResponse or array. Based on queryService return type 'any', 
                // and ExecuteQueryDialog usage (executionResult.data), we need to handle it.
                // Assuming it returns standard list or page content.
                if (Array.isArray(response)) return response;
                if (response && Array.isArray(response.content)) return response.content;
                if (response && Array.isArray(response.data)) return response.data; // generic wrapper
                return [];
            } catch (err) {
                console.error("Failed to fetch results", err);
                return [];
            }
        },
        enabled: !!schedule && !!selectedTerminal && !!schedule.resultTableName,
    });

    React.useEffect(() => {
        if (open && selectedTerminal) {
            refetch();
        }
    }, [open, selectedTerminal, refetch]);

    if (!schedule) return null;

    // Derived columns from first result item if available
    const columns = results && results.length > 0
        ? Object.keys(results[0]).map(k => ({ id: k, label: k }))
        : [];

    return (
        <McDialog
            open={open}
            closed={onClose}
            heading={`Stored Results: Schedule: ${schedule.scheduleName}`}
            width="900px"
        >
            <div style={{ padding: '1rem' }}>
                <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                    Table: <strong>{schedule.resultTableName}</strong>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '2rem' }}>
                    <div style={{ width: '200px' }}>
                        <McSelect
                            label="Terminal"
                            value={selectedTerminal}
                            data={schedule.terminalIds.map((t: string) => ({ label: t, value: t }))}
                            optionselected={(e: any) => setSelectedTerminal(e.detail.value)}
                        />
                    </div>

                    {selectedTerminal && (
                        <McTag appearance="neutral" style={{ marginBottom: '4px' }}>
                            <McIcon icon="file" style={{ marginRight: '6px' }} />
                            Viewing data for {selectedTerminal}
                        </McTag>
                    )}
                </div>

                <div style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa' }}>
                    {isLoading ? (
                        <McLoadingIndicator />
                    ) : (
                        <>
                            {results && results.length > 0 ? (
                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 8px 0 8px' }}>
                                        <DownloadResultsButton data={results} queryId={schedule.resultTableName} />
                                    </div>
                                    <div style={{ width: '100%', height: '400px', overflow: 'auto' }}>
                                    {/* Using McTable if available, otherwise simple table */}
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                                                {columns.map(col => (
                                                    <th key={col.id} style={{ padding: '8px', textAlign: 'left', position: 'sticky', top: 0, background: '#f5f5f5' }}>{col.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.map((row: any, i: number) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                    {columns.map(col => (
                                                        <td key={`${i}-${col.id}`} style={{ padding: '8px' }}>
                                                            {typeof row[col.id] === 'object' ? JSON.stringify(row[col.id]) : String(row[col.id])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#666' }}>
                                    <McIcon icon="list-bullets" color="#e0e0e0" style={{ marginBottom: '1rem', width: '48px', height: '48px' }} />
                                    <h4 style={{ margin: '0 0 0.5rem 0' }}>No Results Found</h4>
                                    <p style={{ margin: 0, opacity: 0.7 }}>This schedule hasn't executed yet or no results were stored.</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <McButton
                slot="secondaryAction"
                variant="outlined"
                appearance="neutral"
                onClick={onClose}
            >
                Close
            </McButton>
            <McButton
                slot="primaryAction"
                variant="filled"
                appearance="primary"
                onClick={() => refetch()}
            >
                <McIcon icon="rotate" slot="prefix" />
                Refresh
            </McButton>
        </McDialog>
    );
};
